const cities = [
  { name: "Wichita Falls", zips: "76301, 76302, 76304, 76305, 76306, 76308, 76309, 76310" },
  { name: "Burkburnett", zips: "76354" },
  { name: "Iowa Park", zips: "76367" },
  { name: "Electra", zips: "76360" },
  { name: "Henrietta", zips: "76365" },
];

const benefits = [
  { icon: "ri-computer-line", title: "Start 100% Online", desc: "No office visit required to begin. Complete your order from your home, barracks, or office in minutes." },
  { icon: "ri-truck-line", title: "Ship Your Documents", desc: "Use overnight shipping to send your documents directly to us — we handle every detail from there." },
  { icon: "ri-shield-check-line", title: "Expert Document Review", desc: "Every application is reviewed by our team before submission to reduce errors and prevent delays." },
  { icon: "ri-calendar-check-line", title: "Reserved Agency Access", desc: "We hold reserved submission slots at the regional passport agency — no public appointment needed." },
];

export default function WichitaFallsLocalAreaSection() {
  return (
    <section id="service-area" className="bg-white py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Service Area</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">
            Serving Wichita Falls &amp; North Texas
          </h2>
        </div>

        <div className="max-w-4xl mx-auto mb-16">
          <div className="bg-[#F7F8FA] rounded-lg p-8 md:p-10 border border-gray-200">
            <p className="text-gray-600 text-base leading-[1.8] mb-5">
              Wichita Falls is defined by its relationship with Sheppard Air Force Base, one of the largest military training installations in the United States. The base is home to the 82nd Training Wing and hosts pilot training programs for NATO allied nations, making it a uniquely international community in the heart of North Texas. For military families stationed here, passport needs are frequent and often urgent — PCS orders arrive with compressed timelines, deployments create leave windows that require family passports to be current, and international training programs bring unique documentation requirements.
            </p>
            <p className="text-gray-600 text-base leading-[1.8] mb-5">
              Beyond the military presence, Wichita Falls is the economic and cultural center of the North Texas/South Oklahoma region. Midwestern State University draws students from across the country and internationally, many of whom need passports for study-abroad semesters and research programs. The city's healthcare sector — anchored by United Regional Health Care System — attracts medical professionals who travel internationally for conferences and continuing education. And the oil and gas industry, deeply embedded in the Permian Basin proximity, creates a class of business travelers with urgent international travel needs tied to energy markets worldwide.
            </p>
            <p className="text-gray-600 text-base leading-[1.8] mb-5">
              The nearest major airport, Dallas/Fort Worth International (DFW), sits about 120 miles south — a two-hour drive that Wichita Falls travelers regularly make for international departures. When a passport expires or needs updating, driving to DFW and back is already on the table. That same logistical awareness makes overnight document shipping to Fast Passport Center a natural fit — no additional trip required, and the processing is handled by professionals who understand military timelines and North Texas travel patterns.
            </p>
            <p className="text-gray-600 text-base leading-[1.8]">
              Fast Passport Center was built for exactly these scenarios. We provide expedited passport service with dedicated agents who understand military PCS cycles, document review that catches errors before they cost you a deployment slot or conference registration, and processing as fast as 24 hours for true emergencies. No driving to Houston or Dallas for a regional agency appointment. Just reliable, predictable service from a registered U.S. Department of State courier.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
          <div>
            <div className="bg-[#1A365D] rounded-lg p-7 mb-8 text-white">
              <div className="flex items-center gap-3 mb-5">
                <div className="w-11 h-11 flex items-center justify-center rounded-full bg-[#F6C344] flex-shrink-0">
                  <i className="ri-passport-line text-[#1A365D] text-xl"></i>
                </div>
                <div>
                  <p className="font-bold text-base">Fast Passport Center — Wichita Falls, TX</p>
                  <p className="text-white/60 text-xs mt-0.5">Serving Wichita Falls &amp; North Texas</p>
                </div>
              </div>
              <p className="text-white/70 text-sm mb-1">
                <i className="ri-map-pin-line text-[#F6C344] mr-1.5"></i>
                Remote service — serving all of Wichita County and surrounding area
              </p>
              <p className="text-white/50 text-xs mb-5 ml-5">Ship documents overnight from anywhere in North Texas</p>
              <div className="border-t border-white/10 pt-5">
                <p className="text-white/80 text-sm leading-relaxed mb-4">
                  Start online, <strong className="text-white">ship your documents overnight</strong> from any FedEx or UPS location in Wichita Falls, and we handle the rest. Most clients receive their passports within 2–9 business days. Military families on PCS timelines are our specialty.
                </p>
                <a href="https://fastpassportcenter.com/checkout/" className="inline-flex items-center gap-2 bg-[#F6C344] hover:bg-[#e5b430] text-[#1A365D] font-bold text-sm px-5 py-2.5 rounded-md transition-all whitespace-nowrap">
                  Start My Passport <i className="ri-arrow-right-line"></i>
                </a>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {benefits.map((b, i) => (
                <div key={i} className="bg-[#F7F8FA] rounded-lg p-5 border border-gray-200">
                  <div className="w-9 h-9 flex items-center justify-center rounded-full bg-[#F6C344]/15 mb-3">
                    <i className={`${b.icon} text-[#1A365D] text-base`}></i>
                  </div>
                  <p className="font-bold text-[#1A365D] text-sm mb-1">{b.title}</p>
                  <p className="text-gray-500 text-xs leading-relaxed">{b.desc}</p>
                </div>
              ))}
            </div>
          </div>

          <div>
            <div className="bg-[#F7F8FA] rounded-lg p-7 mb-6 border border-gray-200">
              <h3 className="font-bold text-[#1A365D] text-sm uppercase tracking-wide mb-5">
                <i className="ri-map-pin-2-line text-[#F6C344] mr-2"></i>
                Communities We Serve
              </h3>
              <div className="space-y-1.5">
                {cities.map((city, i) => (
                  <div key={i} className="flex items-center justify-between py-2.5 px-3 bg-white rounded-md">
                    <div className="flex items-center gap-2.5">
                      <i className="ri-checkbox-circle-fill text-[#F6C344] text-sm"></i>
                      <span className="text-[#1A365D] font-medium text-sm">{city.name}</span>
                    </div>
                    {city.zips && <span className="text-gray-400 text-xs font-mono hidden sm:inline">{city.zips}</span>}
                  </div>
                ))}
              </div>
              <p className="text-gray-500 text-xs mt-4 leading-relaxed">
                Anywhere in the Wichita Falls metro — start online, ship overnight, and we handle the rest. No need to drive to Dallas or Houston for a regional agency appointment.
              </p>
              <div className="mt-4 pt-4 border-t border-gray-100">
                <p className="text-gray-400 text-xs font-semibold uppercase tracking-wider mb-3">Nearby Cities We Serve</p>
                <div className="flex flex-wrap gap-2">
                  {[{ name: "Dallas", href: "/cities/dallas" }, { name: "Fort Worth", href: "/cities/fort-worth" }, { name: "Lubbock", href: "/cities/lubbock" }, { name: "Amarillo", href: "/cities/amarillo" }, { name: "Texas", href: "/states/texas" }].map((link) => (
                    <a key={link.href} href={link.href} className="inline-flex items-center gap-1 text-xs text-[#1A365D] hover:text-[#F6C344] font-medium transition-colors bg-white border border-gray-200 hover:border-[#F6C344] rounded-full px-3 py-1.5 whitespace-nowrap">
                      {link.name} <i className="ri-arrow-right-line text-[10px]"></i>
                    </a>
                  ))}
                </div>
              </div>
            </div>

            <div className="bg-[#1A365D] rounded-lg p-5">
              <p className="text-white/50 text-xs uppercase tracking-widest font-semibold mb-4">Why Wichita Falls Travelers Choose Fast Passport Center</p>
              <div className="grid grid-cols-2 gap-3">
                {[
                  { icon: "ri-government-line", value: "Registered Courier", label: "U.S. Department of State" },
                  { icon: "ri-award-line", value: "A+ Rating", label: "Better Business Bureau" },
                  { icon: "ri-calendar-check-line", value: "20+ Years", label: "Serving Travelers Nationwide" },
                  { icon: "ri-customer-service-2-line", value: "Real Humans", label: "Live Support — Not a Bot" },
                ].map((item, i) => (
                  <a key={i} href="https://fastpassportcenter.com" className="flex items-center gap-2.5 bg-white/5 hover:bg-white/10 rounded-md px-3 py-2.5 transition-colors cursor-pointer">
                    <div className="w-8 h-8 flex items-center justify-center rounded-full bg-[#F6C344]/15 flex-shrink-0">
                      <i className={`${item.icon} text-sm text-[#F6C344]`}></i>
                    </div>
                    <div className="text-left">
                      <span className="text-white text-xs font-bold block leading-tight">{item.value}</span>
                      <span className="text-white/50 text-xs leading-tight block">{item.label}</span>
                    </div>
                  </a>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}